import Phaser from 'phaser';
import { DEPTH } from '../constants';

// ============================================
// VFX System - Tower Weapons & Impact Effects
// Based on tower_weapons_vfx_and_monster_hit_reaction.md
// ============================================

export type VFXType = 
  | 'arrow_projectile' | 'arrow_impact'
  | 'cannon_projectile' | 'cannon_explosion'
  | 'antiair_bullet' | 'antiair_muzzle'
  | 'ice_pulse' | 'ice_particles'
  | 'laser_beam' | 'laser_hit'
  | 'tesla_arc' | 'tesla_impact'
  | 'mortar_projectile' | 'mortar_explosion'
  | 'fire_cone' | 'fire_embers'
  | 'lightning_bolt_up' | 'lightning_bolt_down'
  | 'trap_beam' | 'trap_spark'
  | 'gem_aura' | 'gem_sparkle';

interface ProjectileConfig {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color: number;
  duration?: number;
  size?: number;
  arc?: boolean;
  trail?: boolean;
  /** Called when projectile reaches target (for deferred damage application) */
  onHit?: () => void;
}

interface ImpactConfig {
  x: number;
  y: number;
  color: number;
  radius?: number;
  particleCount?: number;
  duration?: number;
}

class VFXSystemManager {
  private scene: Phaser.Scene | null = null;
  private activeEffects: Set<Phaser.GameObjects.GameObject> = new Set();

  init(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  // ============================================
  // Arrow Tower VFX
  // ============================================

  spawnArrowProjectile(config: ProjectileConfig): void {
    if (!this.scene) return;

    const { startX, startY, endX, endY, color, duration = 150, size = 8 } = config;

    // Create arrow as a triangle/line
    const arrow = this.scene.add.polygon(startX, startY, [
      { x: size, y: 0 },
      { x: -size/2, y: -size/3 },
      { x: -size/2, y: size/3 },
    ], color);
    arrow.setDepth(DEPTH.PROJECTILES);

    // Rotate to face target
    const angle = Phaser.Math.Angle.Between(startX, startY, endX, endY);
    arrow.setRotation(angle);

    // Add trail
    const trail = this.scene.add.graphics();
    trail.setDepth(DEPTH.PROJECTILES - 1);
    this.activeEffects.add(trail);

    // Animate flight
    this.scene.tweens.add({
      targets: arrow,
      x: endX,
      y: endY,
      duration,
      ease: 'Linear',
      onUpdate: () => {
        // Draw fading trail
        trail.clear();
        trail.lineStyle(2, color, 0.5);
        trail.lineBetween(startX, startY, arrow.x, arrow.y);
      },
      onComplete: () => {
        config.onHit?.();
        arrow.destroy();
        trail.destroy();
        this.activeEffects.delete(trail);
        this.spawnArrowImpact({ x: endX, y: endY, color });
      }
    });
  }

  spawnArrowImpact(config: ImpactConfig): void {
    if (!this.scene) return;

    const { x, y, color, particleCount = 8 } = config;

    // Spark burst
    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.circle(x, y, 2, color);
      particle.setDepth(DEPTH.PROJECTILES);

      const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.3;
      const dist = 15 + Math.random() * 10;

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0.3,
        duration: 200,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }
  }

  // ============================================
  // Cannon Tower VFX
  // ============================================

  spawnCannonProjectile(config: ProjectileConfig): void {
    if (!this.scene) return;

    const { startX, startY, endX, endY, color, duration = 400, size = 10 } = config;

    // Create cannonball
    const ball = this.scene.add.circle(startX, startY, size, color);
    ball.setDepth(DEPTH.PROJECTILES);

    // Calculate arc
    const midX = (startX + endX) / 2;
    const midY = Math.min(startY, endY) - 40; // Arc height

    // Animate with arc
    this.scene.tweens.add({
      targets: ball,
      x: midX,
      y: midY,
      duration: duration / 2,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.scene!.tweens.add({
          targets: ball,
          x: endX,
          y: endY,
          duration: duration / 2,
          ease: 'Sine.easeIn',
          onComplete: () => {
            config.onHit?.(); // Applies damage and spawns explosion via Tower.applySplashDamage
            ball.destroy();
            if (!config.onHit) {
              this.spawnCannonExplosion({ x: endX, y: endY, color, radius: 40 });
            }
          }
        });
      }
    });

    // Smoke trail
    const trailInterval = this.scene.time.addEvent({
      delay: 50,
      callback: () => {
        if (ball.active) {
          const smoke = this.scene!.add.circle(ball.x, ball.y, 4, 0x888888, 0.6);
          smoke.setDepth(DEPTH.PROJECTILES - 1);
          this.scene!.tweens.add({
            targets: smoke,
            alpha: 0,
            scale: 2,
            duration: 300,
            onComplete: () => smoke.destroy()
          });
        }
      },
      repeat: Math.floor(duration / 50) - 1
    });
  }

  spawnCannonExplosion(config: ImpactConfig): void {
    if (!this.scene) return;

    const { x, y, color, radius = 40, particleCount = 12 } = config;

    // Expanding ring
    const ring = this.scene.add.circle(x, y, 5, 0x000000, 0);
    ring.setStrokeStyle(3, color);
    ring.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: ring,
      scaleX: radius / 5,
      scaleY: radius / 5,
      alpha: 0,
      duration: 200,
      onComplete: () => ring.destroy()
    });

    // Dust particles
    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.rectangle(
        x, y,
        4 + Math.random() * 4,
        4 + Math.random() * 4,
        0x8b7355
      );
      particle.setDepth(DEPTH.PROJECTILES);

      const angle = Math.random() * Math.PI * 2;
      const dist = radius * (0.5 + Math.random() * 0.5);

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist - 10,
        alpha: 0,
        rotation: Math.random() * Math.PI,
        duration: 300 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => particle.destroy()
      });
    }

    // Small camera shake
    this.scene.cameras.main.shake(80, 0.003);
  }

  // ============================================
  // Anti-Air Tower VFX
  // ============================================

  spawnAntiAirBullet(config: ProjectileConfig): void {
    if (!this.scene) return;

    const { startX, startY, endX, endY, color } = config;

    // Muzzle flash
    const flash = this.scene.add.circle(startX, startY, 6, 0xffff00);
    flash.setDepth(DEPTH.PROJECTILES);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 0.5,
      duration: 60,
      onComplete: () => flash.destroy()
    });

    // Fast bullet line (hitscan style)
    const bullet = this.scene.add.graphics();
    bullet.setDepth(DEPTH.PROJECTILES);
    bullet.lineStyle(2, color);
    bullet.lineBetween(startX, startY, endX, endY);

    this.scene.tweens.add({
      targets: bullet,
      alpha: 0,
      duration: 80,
      onComplete: () => bullet.destroy()
    });

    // Tiny spark at impact
    const spark = this.scene.add.circle(endX, endY, 3, 0xffffff);
    spark.setDepth(DEPTH.PROJECTILES);
    this.scene.tweens.add({
      targets: spark,
      alpha: 0,
      scale: 2,
      duration: 100,
      onComplete: () => spark.destroy()
    });
  }

  // ============================================
  // Ice Tower VFX
  // ============================================

  spawnIcePulse(x: number, y: number, radius: number): void {
    if (!this.scene) return;

    // Expanding frost ring
    const ring = this.scene.add.circle(x, y, 10, 0x00ffff, 0.3);
    ring.setStrokeStyle(2, 0x00ffff);
    ring.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: ring,
      scaleX: radius / 10,
      scaleY: radius / 10,
      alpha: 0,
      duration: 400,
      onComplete: () => ring.destroy()
    });
  }

  spawnIceParticles(x: number, y: number): void {
    if (!this.scene) return;

    // Snow particles drifting down
    for (let i = 0; i < 5; i++) {
      const snow = this.scene.add.circle(
        x + (Math.random() - 0.5) * 20,
        y - 10,
        2,
        0xffffff
      );
      snow.setDepth(DEPTH.PROJECTILES);

      this.scene.tweens.add({
        targets: snow,
        y: y + 20,
        x: snow.x + (Math.random() - 0.5) * 10,
        alpha: 0,
        duration: 600 + Math.random() * 400,
        onComplete: () => snow.destroy()
      });
    }
  }

  // ============================================
  // Tesla Tower VFX
  // ============================================

  spawnTeslaArc(startX: number, startY: number, endX: number, endY: number): void {
    if (!this.scene) return;

    const graphics = this.scene.add.graphics();
    graphics.setDepth(DEPTH.PROJECTILES);

    // Create jagged lightning path
    const points: { x: number; y: number }[] = [{ x: startX, y: startY }];
    const segments = 6;
    const dx = (endX - startX) / segments;
    const dy = (endY - startY) / segments;

    for (let i = 1; i < segments; i++) {
      points.push({
        x: startX + dx * i + (Math.random() - 0.5) * 20,
        y: startY + dy * i + (Math.random() - 0.5) * 20
      });
    }
    points.push({ x: endX, y: endY });

    // Draw glow
    graphics.lineStyle(6, 0x00ffff, 0.3);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.strokePath();

    // Draw core
    graphics.lineStyle(2, 0xffffff);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.strokePath();

    // Electric impact circle
    const impact = this.scene.add.circle(endX, endY, 15, 0x00ffff, 0.5);
    impact.setDepth(DEPTH.PROJECTILES);

    // Fade out
    this.scene.tweens.add({
      targets: [graphics, impact],
      alpha: 0,
      duration: 100,
      onComplete: () => {
        graphics.destroy();
        impact.destroy();
      }
    });
  }

  // ============================================
  // Laser Tower VFX
  // ============================================

  spawnLaserBeam(startX: number, startY: number, endX: number, endY: number): void {
    if (!this.scene) return;

    const graphics = this.scene.add.graphics();
    graphics.setDepth(DEPTH.PROJECTILES);

    // Outer glow
    graphics.lineStyle(6, 0xff00ff, 0.4);
    graphics.lineBetween(startX, startY, endX, endY);

    // Inner core
    graphics.lineStyle(2, 0xffffff);
    graphics.lineBetween(startX, startY, endX, endY);

    // Hit zap
    const zap = this.scene.add.star(endX, endY, 4, 3, 8, 0xffffff);
    zap.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: [graphics, zap],
      alpha: 0,
      duration: 100,
      onComplete: () => {
        graphics.destroy();
        zap.destroy();
      }
    });
  }

  // ============================================
  // Mortar Tower VFX
  // ============================================

  spawnMortarProjectile(config: ProjectileConfig): void {
    if (!this.scene) return;

    const { startX, startY, endX, endY, color, duration = 600, size = 14 } = config;

    // Big shell
    const shell = this.scene.add.circle(startX, startY, size, color);
    shell.setDepth(DEPTH.PROJECTILES);

    // Higher arc than cannon
    const midX = (startX + endX) / 2;
    const midY = Math.min(startY, endY) - 80;

    this.scene.tweens.add({
      targets: shell,
      x: midX,
      y: midY,
      duration: duration / 2,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.scene!.tweens.add({
          targets: shell,
          x: endX,
          y: endY,
          duration: duration / 2,
          ease: 'Sine.easeIn',
          onComplete: () => {
            config.onHit?.();
            shell.destroy();
            if (!config.onHit) {
              this.spawnMortarExplosion({ x: endX, y: endY, color, radius: 60 });
            }
          }
        });
      }
    });

    // Heavier smoke trail
    const trailInterval = this.scene.time.addEvent({
      delay: 40,
      callback: () => {
        if (shell.active) {
          const smoke = this.scene!.add.circle(shell.x, shell.y, 6, 0x666666, 0.7);
          smoke.setDepth(DEPTH.PROJECTILES - 1);
          this.scene!.tweens.add({
            targets: smoke,
            alpha: 0,
            scale: 2.5,
            y: smoke.y - 20,
            duration: 400,
            onComplete: () => smoke.destroy()
          });
        }
      },
      repeat: Math.floor(duration / 40) - 1
    });
  }

  spawnMortarExplosion(config: ImpactConfig): void {
    if (!this.scene) return;

    const { x, y, color, radius = 60, particleCount = 18 } = config;

    // Big expanding ring
    const ring = this.scene.add.circle(x, y, 8, 0x000000, 0);
    ring.setStrokeStyle(4, color);
    ring.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: ring,
      scaleX: radius / 8,
      scaleY: radius / 8,
      alpha: 0,
      duration: 250,
      onComplete: () => ring.destroy()
    });

    // Flash
    const flash = this.scene.add.circle(x, y, radius * 0.8, 0xffffff, 0.6);
    flash.setDepth(DEPTH.PROJECTILES);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 0.5,
      duration: 100,
      onComplete: () => flash.destroy()
    });

    // Debris
    for (let i = 0; i < particleCount; i++) {
      const debris = this.scene.add.rectangle(
        x, y,
        5 + Math.random() * 6,
        5 + Math.random() * 6,
        0x8b7355
      );
      debris.setDepth(DEPTH.PROJECTILES);

      const angle = Math.random() * Math.PI * 2;
      const dist = radius * (0.6 + Math.random() * 0.6);

      this.scene.tweens.add({
        targets: debris,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist - 20,
        alpha: 0,
        rotation: Math.random() * Math.PI * 2,
        duration: 400 + Math.random() * 300,
        ease: 'Power2',
        onComplete: () => debris.destroy()
      });
    }

    // Camera shake (stronger than cannon)
    this.scene.cameras.main.shake(120, 0.005);
  }

  // ============================================
  // Fire Tower VFX
  // ============================================

  spawnFireCone(x: number, y: number, angle: number, range: number): void {
    if (!this.scene) return;

    const coneAngle = Math.PI / 3; // 60 degrees

    // Dense ember/spark particles - simulates flame spray (no solid block)
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const emberAngle = angle + (Math.random() - 0.5) * coneAngle;
      const dist = 15 + Math.random() * (range - 20); // Start near nozzle, spread along cone
      const startX = x + Math.cos(emberAngle) * dist * 0.3;
      const startY = y + Math.sin(emberAngle) * dist * 0.3;
      const colors = [0xff6600, 0xff4500, 0xff3300, 0xff8800, 0xcc2200];
      const emberColor = colors[Math.floor(Math.random() * colors.length)];
      const size = 1.5 + Math.random() * 3;
      const ember = this.scene.add.circle(startX, startY, size, emberColor);
      ember.setDepth(DEPTH.PROJECTILES);

      // Spray forward + slight upward drift (heat rises)
      const forwardDist = dist * (0.6 + Math.random() * 0.5);
      const endX = x + Math.cos(emberAngle) * forwardDist + (Math.random() - 0.5) * 25;
      const endY = y + Math.sin(emberAngle) * forwardDist - 20 - Math.random() * 30;
      const duration = 120 + Math.random() * 180;

      this.scene.tweens.add({
        targets: ember,
        x: endX,
        y: endY,
        alpha: 0,
        scale: 0.3,
        duration,
        ease: 'Sine.easeOut',
        onComplete: () => ember.destroy()
      });
    }
  }

  spawnFireEmbers(x: number, y: number): void {
    if (!this.scene) return;

    for (let i = 0; i < 4; i++) {
      const ember = this.scene.add.circle(
        x + (Math.random() - 0.5) * 10,
        y,
        2,
        0xff6600
      );
      ember.setDepth(DEPTH.PROJECTILES);

      this.scene.tweens.add({
        targets: ember,
        y: y - 20 - Math.random() * 15,
        x: ember.x + (Math.random() - 0.5) * 15,
        alpha: 0,
        scale: 0.5,
        duration: 400 + Math.random() * 300,
        onComplete: () => ember.destroy()
      });
    }
  }

  // ============================================
  // Lightning Tower VFX (global)
  // ============================================

  spawnLightningStrike(
    towerX: number,
    towerY: number,
    targets: Array<{ x: number; y: number }>
  ): void {
    if (!this.scene) return;

    // Step 1: Bolt going up
    const graphics = this.scene.add.graphics();
    graphics.setDepth(DEPTH.PROJECTILES);

    graphics.lineStyle(4, 0x00ffff);
    graphics.lineBetween(towerX, towerY, towerX, 0);

    // Step 2: Bolts coming down to each target (staggered)
    targets.forEach((target, index) => {
      this.scene!.time.delayedCall(50 + index * 20, () => {
        this.spawnTeslaArc(towerX, 0, target.x, target.y);
      });
    });

    // Fade main bolt
    this.scene.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: 150,
      onComplete: () => graphics.destroy()
    });

    // Screen flash
    const flash = this.scene.add.rectangle(
      this.scene.cameras.main.centerX,
      this.scene.cameras.main.centerY,
      this.scene.cameras.main.width,
      this.scene.cameras.main.height,
      0xffffff, 0.15
    );
    flash.setScrollFactor(0);
    flash.setDepth(DEPTH.OVERLAY);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 100,
      onComplete: () => flash.destroy()
    });
  }

  // ============================================
  // Trap Tower VFX
  // ============================================

  spawnTrapBeam(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.scene) return;

    const graphics = this.scene.add.graphics();
    graphics.setDepth(DEPTH.PROJECTILES);

    // Main beam with jitter
    const jitterAmount = 2;
    const midX = (x1 + x2) / 2 + (Math.random() - 0.5) * jitterAmount;
    const midY = (y1 + y2) / 2 + (Math.random() - 0.5) * jitterAmount;

    // Outer glow
    graphics.lineStyle(6, 0x00ff00, 0.3);
    graphics.lineBetween(x1, y1, midX, midY);
    graphics.lineBetween(midX, midY, x2, y2);

    // Inner core
    graphics.lineStyle(2, 0xffffff);
    graphics.lineBetween(x1, y1, midX, midY);
    graphics.lineBetween(midX, midY, x2, y2);

    // Pulse traveling along beam
    const pulse = this.scene.add.circle(x1, y1, 4, 0x00ff00);
    pulse.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: pulse,
      x: x2,
      y: y2,
      duration: 500,
      repeat: -1
    });

    // Store for cleanup
    this.activeEffects.add(graphics);
    this.activeEffects.add(pulse);

    // Auto cleanup after a frame (continuous effects need external management)
    this.scene.time.delayedCall(50, () => {
      graphics.destroy();
      pulse.destroy();
      this.activeEffects.delete(graphics);
      this.activeEffects.delete(pulse);
    });
  }

  spawnTrapSpark(x: number, y: number): void {
    if (!this.scene) return;

    for (let i = 0; i < 4; i++) {
      const spark = this.scene.add.circle(x, y, 2, 0x00ff00);
      spark.setDepth(DEPTH.PROJECTILES);

      const angle = Math.random() * Math.PI * 2;
      const dist = 10 + Math.random() * 10;

      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        duration: 150,
        onComplete: () => spark.destroy()
      });
    }
  }

  // ============================================
  // Gem Tower VFX
  // ============================================

  spawnGemAura(x: number, y: number, radius: number): void {
    if (!this.scene) return;

    const ring = this.scene.add.circle(x, y, radius, 0xffffff, 0.1);
    ring.setStrokeStyle(2, 0xffd700);
    ring.setDepth(DEPTH.Y_SORT_BASE - 1);

    // Pulsing animation
    this.scene.tweens.add({
      targets: ring,
      scale: 1.1,
      alpha: 0.2,
      duration: 800,
      yoyo: true,
      repeat: -1
    });

    // Store for cleanup
    this.activeEffects.add(ring);
  }

  spawnGemSparkle(fromX: number, fromY: number, toX: number, toY: number): void {
    if (!this.scene) return;

    const sparkle = this.scene.add.star(fromX, fromY, 4, 2, 6, 0xffd700);
    sparkle.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: sparkle,
      x: toX,
      y: toY,
      alpha: 0,
      duration: 400,
      ease: 'Sine.easeOut',
      onComplete: () => sparkle.destroy()
    });
  }

  // ============================================
  // Generic Effects
  // ============================================

  spawnHitSpark(x: number, y: number, color: number = 0xffffff): void {
    if (!this.scene) return;

    const spark = this.scene.add.star(x, y, 4, 2, 6, color);
    spark.setDepth(DEPTH.PROJECTILES);

    this.scene.tweens.add({
      targets: spark,
      alpha: 0,
      scale: 1.5,
      rotation: Math.PI / 4,
      duration: 150,
      onComplete: () => spark.destroy()
    });
  }

  spawnDamageNumber(x: number, y: number, damage: number, color: number = 0xffffff): void {
    if (!this.scene) return;

    const text = this.scene.add.text(x, y - 10, `-${Math.round(damage)}`, {
      fontSize: '14px',
      color: Phaser.Display.Color.IntegerToColor(color).rgba,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    });
    text.setOrigin(0.5);
    text.setDepth(DEPTH.UI_TEXT);

    this.scene.tweens.add({
      targets: text,
      y: y - 40,
      alpha: 0,
      duration: 800,
      ease: 'Power1',
      onComplete: () => text.destroy()
    });
  }

  // ============================================
  // Cleanup
  // ============================================

  reset(): void {
    this.activeEffects.forEach(effect => {
      if (effect && effect.active) {
        effect.destroy();
      }
    });
    this.activeEffects.clear();
  }

  destroy(): void {
    this.reset();
    this.scene = null;
  }
}

// Singleton
export const VFXSystem = new VFXSystemManager();
